import React, { useState } from 'react';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { SearchTextField } from 'src/components/BeaconV2Components/SearchInput/SearchInputBar';

import { useChartComparison } from 'src/components/BeaconV2Components/BeaconMetricSplineChart/ChartComparisonProvider';
import { useStacklineTheme } from '@stackline/ui';
import CompareScrollableContainer from 'src/components/BeaconV2Components/BeaconChartWithLegend/ComparePopup/CompareScrollableContainer';
import CompareOption from 'src/components/BeaconV2Components/BeaconChartWithLegend/ComparePopup/CompareOption';

export interface CompareMetricOption {
  label: string;
  indexName: string;
  fieldName: string;
}

const trafficOptions: CompareMetricOption[] = [
  {
    label: 'Total',
    indexName: 'traffic',
    fieldName: 'totalClicks'
  },
  {
    label: 'Organic Traffic',
    indexName: 'traffic',
    fieldName: 'organicClicks'
  },
  {
    label: 'Paid Traffic',
    indexName: 'advertising',
    fieldName: 'clicks'
  },
  {
    label: 'Paid Traffic Spend',
    indexName: 'advertising',
    fieldName: 'spend'
  }
];

const conversionOptions: CompareMetricOption[] = [
  // TODO Add conversion rate here
  {
    label: 'Retail Price',
    indexName: 'sales',
    fieldName: 'retailPrice'
  },

  {
    label: 'Content Score',
    indexName: 'sales',
    fieldName: 'contentScore'
  },
  {
    label: 'Content Score',
    indexName: 'sales',
    fieldName: 'ratings'
  }
];

const salesOptions: CompareMetricOption[] = [
  {
    label: 'Retail Sales',
    indexName: 'sales',
    fieldName: 'retailSales'
  },

  {
    label: 'Units Sold',
    indexName: 'sales',
    fieldName: 'unitsSold'
  }
];

const compareMetricOptions = [
  {
    title: 'Traffic',
    options: trafficOptions
  },
  {
    title: 'Conversion',
    options: conversionOptions
  },
  {
    title: 'Sales',
    options: salesOptions
  }
];

interface AtlasV2CompareMetricsProps {
  handleClose: () => void;
}

/**
 * Compare popup inner content for comparing metrics
 */
export default function AtlasV2CompareMetrics({ handleClose }: AtlasV2CompareMetricsProps) {
  const [metricSearch, setMetricSearch] = useState('');
  const { setMetricComparator, metricComparator } = useChartComparison();
  const theme = useStacklineTheme();

  const handleSelectOption = (option: CompareMetricOption) => {
    setMetricComparator({
      fieldName: option.fieldName,
      indexName: option.indexName
    });
    handleClose();
  };

  const filteredOptions = compareMetricOptions
    .map((option) => ({
      ...option,
      options: option.options.filter((metricOption) =>
        metricOption.label.toLowerCase().includes(metricSearch.toLowerCase())
      )
    }))
    .filter((option) => option.options.length > 0);

  return (
    <Flex flexDirection="column" gap="md">
      <Text variant="subtitle1">Metrics</Text>
      <SearchTextField
        placeholder="Search metrics"
        value={metricSearch}
        onChange={(e) => setMetricSearch(e.target.value)}
        sx={{
          width: '100%'
        }}
      />
      <CompareScrollableContainer height="417px">
        {filteredOptions.map((option) => (
          <React.Fragment key={option.title}>
            <Flex alignItems="center" paddingY={theme.spacing.sm} paddingLeft="12px">
              <Text variant="subtitle2">{option.title}</Text>
            </Flex>
            {option.options.map((metricOption) => (
              <CompareOption
                key={metricOption.label}
                label={metricOption.label}
                selected={metricComparator && metricComparator.fieldName === metricOption.fieldName}
                onClick={() => handleSelectOption(metricOption)}
                sx={{
                  paddingLeft: '24px'
                }}
              />
            ))}
          </React.Fragment>
        ))}
      </CompareScrollableContainer>
    </Flex>
  );
}
